import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const medplumDisabledResponseSchema = z.object({
  enabled: z.literal(false),
});

/**
 * Summary of the AccessPolicy the server attached to the caller's Medplum
 * ProjectMembership during this setup call. Rendered on the FHIR Profile
 * tab so the user can see what access they actually have — the truth comes
 * from this response, not from client-side role inference.
 */
const medplumAccessSummarySchema = z.object({
  /** 'broad' = no AccessPolicy attached; 'scoped-gp' = Patient?general-practitioner=%profile. */
  level: z.enum(['broad', 'scoped-gp']),
  /** Which role branch drove the decision. */
  reason: z.enum(['eluve-admin', 'tenant-admin', 'tenant-member']),
});

const medplumSetupResponseSchema = z.object({
  clientId: z.string(),
  projectId: z.string(),
  practitionerId: z.string().nullable(),
  access: medplumAccessSummarySchema.nullable(),
});

const medplumEnableResponseSchema = z.object({
  projectId: z.string(),
  clientId: z.string(),
  status: z.enum(['enabled', 'already_enabled']),
});

const medplumConfigResponseSchema = z.discriminatedUnion('enabled', [
  z.object({
    enabled: z.literal(true),
    projectId: z.string(),
    clientId: z.string(),
  }),
  medplumDisabledResponseSchema,
]);

const medplumCreateUserResponseSchema = z.union([
  z.object({
    status: z.literal('created'),
    practitionerId: z.string(),
    membershipId: z.string(),
  }),
  z.object({
    status: z.literal('already_exists'),
    practitionerId: z.string(),
  }),
]);

/**
 * `already_exists` = patients.medplum_resource_id was already filled, so no
 * Medplum round-trip happened. `created` = the identifier-keyed upsert ran —
 * it may have matched an existing FHIR Patient by identifier, but from the
 * caller's perspective the resource is now guaranteed to exist either way.
 */
const medplumEnsurePatientResponseSchema = z.object({
  fhirPatientId: z.string(),
  status: z.enum(['created', 'already_exists']),
});

const medplumCreateProjectResponseSchema = z.object({
  projectId: z.string(),
  clientId: z.string(),
  status: z.enum(['created', 'already_exists']),
});

const medplumCreateUserErrorResponseSchema = z.object({
  error: z.string(),
});

const medplumPatientStatusResponseSchema = z.object({
  /** FHIR Patient versionId after the write (unchanged on a no-op). */
  versionId: z.string(),
});

const medplumProjectSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  tenantId: z.string().nullable(),
});

const medplumDiagnosticsResponseSchema = z.object({
  healthy: z.boolean(),
  projects: z.array(medplumProjectSummarySchema),
  serverVersion: z.string().nullable(),
  error: z.string().nullable(),
});

export const medplumAdminContract = c.router(
  {
    diagnostics: {
      method: 'GET',
      path: '/diagnostics',
      responses: {
        200: medplumDiagnosticsResponseSchema,
      },
      summary: 'Get Medplum diagnostics: health check and list all projects',
    },
  },
  { pathPrefix: '/medplum' },
);

export const medplumContract = c.router(
  {
    setup: {
      method: 'POST',
      path: '/setup',
      body: z.object({}),
      responses: {
        200: z.union([
          medplumDisabledResponseSchema,
          medplumSetupResponseSchema,
        ]),
      },
      summary: 'Setup Medplum project and user for the current tenant',
    },
    createProject: {
      method: 'POST',
      path: '/project',
      body: z.object({}),
      responses: {
        200: medplumCreateProjectResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary: 'Create Medplum project for the tenant',
    },
    enable: {
      method: 'POST',
      path: '/enable',
      body: z.object({}),
      responses: {
        200: z.union([
          medplumDisabledResponseSchema,
          medplumEnableResponseSchema,
        ]),
      },
      summary: 'Enable Medplum for tenant (admin only)',
    },
    getConfig: {
      method: 'GET',
      path: '/config',
      responses: {
        200: medplumConfigResponseSchema,
      },
      summary: 'Get Medplum configuration for tenant',
    },
    ensurePatient: {
      method: 'POST',
      path: '/patients/:patientId/ensure',
      body: z.object({}),
      pathParams: z.object({
        tenantId: z.string(),
        patientId: z.string(),
      }),
      responses: {
        200: medplumEnsurePatientResponseSchema,
        404: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        'Idempotently ensure a FHIR Patient exists for the Eluve patient',
    },
    setPatientStatus: {
      method: 'POST',
      path: '/patients/:patientId/status',
      pathParams: z.object({
        tenantId: z.string(),
        patientId: z.string(),
      }),
      body: z.object({
        status: z.enum(['deceased', 'left-practice']),
        set: z.boolean(),
        /** Optimistic lock: reject with 409 when the FHIR Patient has moved past this version. */
        expectedVersionId: z.string().optional(),
      }),
      responses: {
        200: medplumPatientStatusResponseSchema,
        404: medplumCreateUserErrorResponseSchema,
        409: medplumCreateUserErrorResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary:
        'Set or clear a patient status flag (deceased / left practice) in FHIR and the Hasura mirror',
    },
    createUser: {
      method: 'POST',
      path: '/users/:userId/create',
      body: z.object({}),
      pathParams: z.object({
        tenantId: z.string(),
        userId: z.string(),
      }),
      responses: {
        200: medplumCreateUserResponseSchema,
        422: medplumCreateUserErrorResponseSchema,
      },
      summary: 'Create a Medplum user for the specified user',
    },
  },
  { pathPrefix: '/:tenantId/medplum' },
);
